const convertPatientDataToAddressMaintenance = (patientData) => {
  if (!patientData) {
    return [];
  }

  // Helper function to detect if address is a PO Box
  const isPOBox = (street) => {
    if (!street || !Array.isArray(street)) return false;
    const streetText = street.join(' ').toLowerCase();
    return (
      streetText.includes('po box') ||
      streetText.includes('p.o. box') ||
      streetText.includes('post office box') ||
      streetText.includes('p o box')
    );
  };

  // Use a Map to group addresses by ID
  const addressMap = new Map();

  // Helper function to add or update address in the map
  const processAddress = (addressData, addressType) => {
    if (!addressData) return;

    const addressId =
      addressData.id || `${patientData.id}_${addressType}`;

    if (addressMap.has(addressId)) {
      // Address with this ID already exists, just update the type flags
      const existingAddress = addressMap.get(addressId);
      existingAddress[
        `is${
          addressType.charAt(0).toUpperCase() + addressType.slice(1)
        }`
      ] = true;
    } else {
      // Create new address entry
      const addressEntry = {
        value: {
          id: addressId,
          address: {
            street: addressData.street || [],
            city: addressData.city || '',
            state: addressData.state || '',
            stateCode: addressData.state || '',
            postalCode: addressData.postcode || '',
            postcode: addressData.postcode || '',
            country:
              addressData.country ||
              patientData.country ||
              'United States',
          },
          isPO: isPOBox(addressData.street),
        },
        isBilling: addressType === 'billing',
        isMailing: addressType === 'mailing',
        isShipping: addressType === 'shipping',
        countryIso2Code: addressData.countryCode || 'US',
      };

      addressMap.set(addressId, addressEntry);
    }
  };

  // Process each address type
  processAddress(patientData.shippingAddress, 'shipping');
  processAddress(patientData.billingAddress, 'billing');
  processAddress(patientData.mailingAddress, 'mailing');

  // Convert Map to array
  return Array.from(addressMap.values());
};

// Enhanced version with more address types support
const convertPatientDataToAddressMaintenanceEnhanced = (
  patientData,
) => {
  if (!patientData) {
    return [];
  }

  const addressMaintenanceData = [];
  const processedAddresses = new Set(); // To avoid duplicates

  // Helper functions
  const isPOBox = (street) => {
    if (!street || !Array.isArray(street)) return false;
    const streetText = street.join(' ').toLowerCase();
    return (
      streetText.includes('po box') ||
      streetText.includes('p.o. box') ||
      streetText.includes('post office box') ||
      streetText.includes('p o box')
    );
  };

  const getAddressKey = (address) => {
    if (!address) return null;
    return `${JSON.stringify(address.street)}-${address.city}-${
      address.state
    }-${address.postcode}-${address.countryCode}`;
  };

  const createAddressObject = (
    address,
    addressType,
    patientId,
    index = '',
  ) => {
    return {
      value: {
        addressId: `${patientId}_${addressType}${index}`,
        address: {
          street: address.street || [],
          city: address.city || '',
          state: address.state || '',
          stateCode: address.state || '',
          postalCode: address.postcode || '',
          postcode: address.postcode || '',
          country:
            address.country || patientData.country || 'United States',
        },
        isPO: isPOBox(address.street),
      },
      isBilling: addressType === 'billing',
      isMailing: addressType === 'main' || addressType === 'mailing',
      isShipping: addressType === 'shipping',
      countryIso2Code: address.countryCode || 'US',
    };
  };

  // Process main address
  if (patientData.address) {
    const addressKey = getAddressKey(patientData.address);
    if (addressKey && !processedAddresses.has(addressKey)) {
      const mainAddress = createAddressObject(
        patientData.address,
        'main',
        patientData.id,
      );
      addressMaintenanceData.push(mainAddress);
      processedAddresses.add(addressKey);
    }
  }

  // Process billing address
  if (patientData.billingAddress) {
    const addressKey = getAddressKey(patientData.billingAddress);

    if (addressKey) {
      if (processedAddresses.has(addressKey)) {
        // Mark existing address as billing too
        const existingAddress = addressMaintenanceData.find(
          (addr) =>
            getAddressKey({
              street: addr.value.address.street,
              city: addr.value.address.city,
              state: addr.value.address.state,
              postcode: addr.value.address.postalCode,
              countryCode: addr.countryIso2Code,
            }) === addressKey,
        );
        if (existingAddress) {
          existingAddress.isBilling = true;
        }
      } else {
        // Add new billing address
        const billingAddress = createAddressObject(
          patientData.billingAddress,
          'billing',
          patientData.id,
        );
        addressMaintenanceData.push(billingAddress);
        processedAddresses.add(addressKey);
      }
    }
  }

  // Process shipping address (if it exists in your data structure)
  if (patientData.shippingAddress) {
    const addressKey = getAddressKey(patientData.shippingAddress);

    if (addressKey && !processedAddresses.has(addressKey)) {
      const shippingAddress = createAddressObject(
        patientData.shippingAddress,
        'shipping',
        patientData.id,
      );
      addressMaintenanceData.push(shippingAddress);
      processedAddresses.add(addressKey);
    }
  }

  return addressMaintenanceData;
};

export {
  convertPatientDataToAddressMaintenance,
  convertPatientDataToAddressMaintenanceEnhanced,
};
